import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  User, 
  AtSign, 
  Phone, 
  Lock, 
  Eye, 
  EyeOff,
  Loader2,
  ChevronLeft,
  Save,
  Camera,
  Upload,
  Mail
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { API } from "../utils/api";

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading: authLoading, updateUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.full_name || user.name || "",
        username: user.username || "",
        email: user.email || "",
        phone: user.phone || ""
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido. Use JPG, PNG, WebP o GIF");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 5MB");
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/auth/upload-avatar`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      updateUser({ avatar: response.data.avatar });
      toast.success("Foto de perfil actualizada");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al subir la imagen";
      toast.error(message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password) {
      if (formData.new_password !== formData.confirm_password) {
        toast.error("Las contraseñas no coinciden");
        return;
      }
      if (formData.new_password.length < 6) {
        toast.error("La nueva contraseña debe tener al menos 6 caracteres");
        return;
      }
      if (!formData.current_password) {
        toast.error("Debes ingresar tu contraseña actual");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null
      };

      // Allow setting username if not set yet (Apple/Google users)
      if (formData.username && !user?.username) {
        payload.username = formData.username;
      }

      if (formData.new_password && formData.current_password) {
        payload.current_password = formData.current_password;
        payload.new_password = formData.new_password;
      }

      const response = await axios.put(`${API}/auth/profile`, payload);
      
      if (response.data.user) {
        updateUser(response.data.user);
      }
      
      toast.success("Perfil actualizado correctamente");
      
      setFormData(prev => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: ""
      }));
    } catch (error) {
      const message = error.response?.data?.detail || "Error al actualizar el perfil";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) return null;

  const avatarUrl = user.avatar 
    ? (user.avatar.startsWith('http') ? user.avatar : `${window.location.origin}${user.avatar}`)
    : null;

  return (
    <div className="pt-20 min-h-screen" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('profile.back') || "Volver"}
        </button>

        <div className="dashboard-card">
          {/* Header with Avatar */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10">
            <div className="relative group">
              <div 
                className="w-20 h-20 rounded-full bg-violet-500/20 flex items-center justify-center overflow-hidden cursor-pointer transition-all group-hover:ring-2 group-hover:ring-violet-500"
                onClick={handleAvatarClick}
                data-testid="avatar-container"
              >
                {uploadingAvatar ? (
                  <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
                ) : avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={user?.full_name || user?.name || ""} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-violet-400">
                    {(user?.full_name || user?.name || user?.username || "U").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div 
                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={handleAvatarClick}
              >
                <Camera className="w-5 h-5 text-white" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleAvatarChange}
                className="hidden"
                data-testid="avatar-input"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{t('profile.title')}</h1>
              <p className="text-gray-400">@{user.username}</p>
              <button
                onClick={handleAvatarClick}
                className="mt-1 flex items-center gap-1 text-sm text-violet-400 hover:text-violet-300 transition-colors"
                data-testid="change-avatar-btn"
              >
                <Upload className="w-3 h-3" />
                {t('profile.changePhoto')}
              </button>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="text-lg font-semibold text-white">{t('profile.personalInfo')}</h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300 mb-2 block">{t('profile.fullName')}</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    required
                    className="pl-12 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">{t('profile.username')}</Label>
                <div className="relative">
                  <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="text"
                    value={formData.username}
                    onChange={!user?.username ? (e) => setFormData({...formData, username: e.target.value}) : undefined}
                    disabled={!!user?.username}
                    className={`pl-12 bg-[#15121e] border-white/10 ${user?.username ? 'text-gray-500 cursor-not-allowed' : 'text-white'}`}
                    placeholder="tu_usuario"
                  />
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  {user?.username ? t('profile.usernameCannotChange') : "Elige un nombre de usuario"}
                </p>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">{t('profile.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    className="pl-12 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="profile-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">{t('profile.phone')}</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+34 600 000 000"
                    className="pl-12 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Password Section */}
            <div className="space-y-4 pt-6 border-t border-white/10">
              <h2 className="text-lg font-semibold text-white">{t('profile.changePassword')}</h2>
              <p className="text-gray-500 text-sm">{t('profile.changePasswordHint')}</p>
              
              <div>
                <Label className="text-gray-300 mb-2 block">{t('profile.currentPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    name="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.current_password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="pl-12 pr-12 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="profile-current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">{t('profile.newPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    name="new_password"
                    type={showNewPassword ? "text" : "password"}
                    value={formData.new_password}
                    onChange={handleChange}
                    placeholder={t('auth.minChars') || "Mínimo 6 caracteres"}
                    className="pl-12 pr-12 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="profile-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">{t('profile.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    name="confirm_password"
                    type={showNewPassword ? "text" : "password"}
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder={t('profile.confirmPassword')}
                    className="pl-12 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="profile-confirm-password-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button
                type="submit"
                className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white glow-violet-hover"
                disabled={loading}
                data-testid="profile-save-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('profile.updating')}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {t('profile.saveChanges')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
